import type {StateCreator, StoreApi} from "zustand/vanilla";
import {RemoteValue} from "../util/RemoteValue";

export function derivedStore<InputStores extends {}, Store>(
    storeApis: { [K in keyof InputStores]: StoreApi<InputStores[K]> },
    transform: (inputStores: InputStores) => Store,
    refreshTransform: (inputStores: InputStores, prevStore: Store) => Store | Promise<Store> = transform,
): StateCreator<Store> {
    const inputStoreApis = Object.entries(storeApis) as
        [keyof InputStores, StoreApi<InputStores[keyof InputStores]>][];

    function inputState(): InputStores {
        return Object.fromEntries(
            inputStoreApis.map(([key, storeApi]) => [key, storeApi.getState()])
        ) as InputStores;
    }

    return (set, get) => {
        inputStoreApis.forEach(([key, storeApi]) =>
            storeApi.subscribe(async newState => {
                const prevStore = get();
                const updatedValue = await refreshTransform({
                    ...inputState(),
                    [key]: newState,
                }, prevStore);
                set(updatedValue);
            })
        )
        return transform(inputState());
    };
}

function combineRemoteValues<IV extends {}, OV>(
    inputs: { [K in keyof IV]: { remoteValue: RemoteValue<IV[K]> } },
    transform: (inputs: IV) => (OV | Promise<OV>),
): RemoteValue<OV> {
    const c = Object.entries(inputs) as
        [keyof IV, { remoteValue: RemoteValue<IV[keyof IV]> }][];
    const d = c.map(([key, {remoteValue}]) => [key, remoteValue]);
    const e = Object.fromEntries(d) as { [K in keyof IV]: RemoteValue<IV[K]> };
    const f = RemoteValue.combine(e) as RemoteValue<IV>;
    return f.map(transform)
}

export function derivedRemoteStore<IV extends {}, OV>(
    inputStoreApis: { [K in keyof IV]: StoreApi<{ remoteValue: RemoteValue<IV[K]> }> },
    transform: (inputs: IV) => (OV | Promise<OV>),
): StateCreator<{ remoteValue: RemoteValue<OV> }> {
    return derivedStore<{ [K in keyof IV]: { remoteValue: RemoteValue<IV[K]> } }, { remoteValue: RemoteValue<OV> }>(
        inputStoreApis,
        (inputStores: { [K in keyof IV]: { remoteValue: RemoteValue<IV[K]> } }) => {
            return {remoteValue: combineRemoteValues(inputStores, transform)};
        },
        async (inputStores: { [K in keyof IV]: { remoteValue: RemoteValue<IV[K]> } }, prevStore) => {
            const newRemoteValue = combineRemoteValues(inputStores, transform);
            const bgRefreshedValue = await prevStore.remoteValue.patchRemote(newRemoteValue);
            return {remoteValue: bgRefreshedValue};
        },
    );
}
